import React from "react";
import ReportingFilters from "@src/reporting/components/ReportingFilters";
import ReportingApplicationsWidget from "@src/reporting/widgets/ReportingApplicationsWidget";
import { can } from "@src/security/permissions";
import { REPORT_MODES } from "@src/reporting/model/reportingConstants.mjs";
import {
  cloneReportingFilter,
  createEmptyReportingFilter,
  reportingFilterForMode,
  reportingFilterForOperators,
} from "@src/reporting/model/reportingFilters.mjs";
import DemographicsWidget from "@src/reporting/widgets/DemographicsWidget";
import DepartmentCapacityUseWidget from "@src/reporting/widgets/DepartmentCapacityUseWidget";
import GeographyDistributionWidget from "@src/reporting/widgets/GeographyDistributionWidget";
import OperatorActivityWidget from "@src/reporting/widgets/OperatorActivityWidget";
import RegistrationTimelineWidget from "@src/reporting/widgets/RegistrationTimelineWidget";
import ReportingSummaryWidget from "@src/reporting/widgets/ReportingSummaryWidget";
import VotingCenterCapacityWidget from "@src/reporting/widgets/VotingCenterCapacityWidget";

import styles from "@src/reporting/Reporting.module.styl";

const ReportingAdvancedPage = ({ gui }) => {
  const [reportMode, setReportMode] = React.useState(REPORT_MODES.STANDARD);
  const [draftFilter, setDraftFilter] = React.useState(createEmptyReportingFilter);
  const [appliedFilter, setAppliedFilter] = React.useState(createEmptyReportingFilter);
  const [refreshToken, setRefreshToken] = React.useState(0);

  const apply = (nextFilter) => {
    const cloned = cloneReportingFilter(nextFilter);
    setDraftFilter(cloned);
    setAppliedFilter(cloned);
    setRefreshToken((value) => value + 1);
  };

  const reset = () => apply(createEmptyReportingFilter());
  const drillDown = (nextFilter) => apply(nextFilter);

  const changeReportMode = (nextMode) => {
    setReportMode(nextMode);
    setRefreshToken((value) => value + 1);
  };

  const reportFilter = reportingFilterForMode(appliedFilter, reportMode);
  const operatorFilter = reportingFilterForOperators(appliedFilter);
  return (
    <div className={styles.page}>
      <ReportingFilters
        gui={gui}
        mode="advanced"
        reportMode={reportMode}
        value={draftFilter}
        onChange={setDraftFilter}
        onApply={apply}
        onReset={reset}
        onReportModeChange={changeReportMode}
      />

      <ReportingSummaryWidget
        filter={reportFilter}
        refreshToken={refreshToken}
      />

      {(reportFilter.dateFrom || reportFilter.dateTo) && can(gui, "SEARCH_VOTER_APPLICATION") ? (
        <ReportingApplicationsWidget
          gui={gui}
          filter={reportFilter}
          refreshToken={refreshToken}
        />
      ) : null}

      <RegistrationTimelineWidget
        filter={reportFilter}
        refreshToken={refreshToken}
        allowIntervalSelection
      />

      <DemographicsWidget
        filter={reportFilter}
        refreshToken={refreshToken}
      />

      <GeographyDistributionWidget
        filter={reportFilter}
        refreshToken={refreshToken}
        allowLevelSelection
        allowDrillDown
        showMap
        showTable
        onDrillDown={drillDown}
      />

      <DepartmentCapacityUseWidget
        filter={reportFilter}
        refreshToken={refreshToken}
      />

      <OperatorActivityWidget
        filter={operatorFilter}
        refreshToken={refreshToken}
      />

      <VotingCenterCapacityWidget
        gui={gui}
        filter={reportFilter}
        refreshToken={refreshToken}
      />
    </div>
  );
};

export const displayAdvancedReporting = (gui) => ({
  uuid: "reporting-advanced",
  view: () => <ReportingAdvancedPage gui={gui} />,
});

export const displayReporting = displayAdvancedReporting;

export default ReportingAdvancedPage;